import React from "react";
import LegalLayout from "../components/site/LegalLayout";
import {
  Section,
  Callout,
  DocumentInfo,
  StepTimeline,
  BadgeTable,
  CardGrid,
  SupportCard,
  CompanyCard,
} from "../components/site/LegalBlocks";
import { COMPANY } from "../data/site";

const TOC = [
  { id: "introduction", title: "Introduction" },
  { id: "purpose", title: "Purpose and Objectives" },
  { id: "scope", title: "Scope and Applicability" },
  { id: "definitions", title: "Definitions" },
  { id: "settlement-principles", title: "Settlement Principles" },
  { id: "eligibility", title: "Merchant Settlement Eligibility" },
  { id: "balance-classification", title: "Merchant Wallet and Balance Classification" },
  { id: "bank-account", title: "Merchant Bank Account Registration" },
  { id: "payout-methods", title: "Settlement and Payout Methods" },
  { id: "settlement-cycles", title: "Settlement Cycles and Processing Timelines" },
  { id: "payout-requests", title: "Merchant Payout Requests" },
  { id: "status-tracking", title: "Settlement Status and Tracking" },
  { id: "charges", title: "Settlement Charges and Deductions" },
  { id: "commission-settlement", title: "Commission Settlement" },
  { id: "financial-service-settlements", title: "Banking and Financial Service Settlements" },
  { id: "reconciliation", title: "Transaction Reconciliation" },
  { id: "failed-transactions", title: "Failed, Pending, and Reversed Transactions" },
  { id: "refunds", title: "Refunds and Wallet Adjustments" },
  { id: "holds", title: "Settlement Holds and Restrictions" },
  { id: "negative-balances", title: "Negative Balances and Financial Recoveries" },
  { id: "settlement-security", title: "Fraud Prevention and Settlement Security" },
  { id: "merchant-responsibilities", title: "Merchant Responsibilities" },
  { id: "platform-responsibilities", title: "Platform Responsibilities" },
  { id: "disputes", title: "Settlement Disputes and Grievance Redressal" },
  { id: "account-closure", title: "Merchant Account Closure and Final Settlement" },
  { id: "records", title: "Financial Records and Audit" },
  { id: "changes", title: "Changes to Settlement Arrangements" },
  { id: "governing-law", title: "Governing Law" },
  { id: "acceptance", title: "Policy Acceptance" },
  { id: "company-information", title: "Company Information" },
];

const DOCUMENT_INFO = [
  ["Document Title", "Merchant Settlement & Payout Policy"],
  ["Version", "1.0"],
  ["Applicable Platform", "JayesPay Web & Mobile Applications"],
  ["Jurisdiction", "India"],
  ["Effective Date", "26 October 2026"],
  ["Document Status", "Active"],
];

const DEFINITIONS = [
  [
    "Merchant",
    "An individual or business entity registered and approved to use eligible JayesPay services.",
  ],
  [
    "Merchant Wallet",
    "A platform-recorded balance or ledger representing funds available or allocated for permitted merchant activities, subject to the actual legal and banking arrangements.",
  ],
  [
    "Available Balance",
    "The portion of a recorded balance eligible for its designated use, after applicable adjustments, reserves, pending transactions, or restrictions.",
  ],
  [
    "Withdrawable Balance",
    "The portion of a balance specifically permitted to be transferred to a merchant's verified bank account under the applicable product terms and fund-holding arrangement.",
  ],
  [
    "Commission",
    "Compensation payable to an eligible merchant for qualifying services or transactions under an applicable commercial arrangement.",
  ],
  [
    "Settlement",
    "The financial completion, accounting, or transfer of an eligible obligation between authorized participants according to the applicable service arrangement.",
  ],
  ["Payout", "The transfer of an eligible payable amount to an approved merchant bank account."],
  [
    "Settlement Cycle",
    "The applicable schedule or processing window for a specific type of settlement.",
  ],
  [
    "Reconciliation",
    "The process of matching platform records with bank, payment partner, service provider, and other relevant transaction records.",
  ],
  [
    "Reversal",
    "An accounting or payment adjustment intended to correct a failed, duplicated, invalid, or otherwise reversible transaction.",
  ],
  [
    "Refund",
    "The return of eligible funds following an applicable refund entitlement or approved transaction outcome.",
  ],
  [
    "Settlement Hold",
    "A temporary restriction on processing an identified payout or settlement amount for a documented operational, contractual, regulatory, legal, or risk-related reason.",
  ],
];

const PRINCIPLES = [
  {
    title: "5.1 Accuracy",
    body: "Merchant settlements shall be based on valid transaction and financial records.",
  },
  {
    title: "5.2 Transparency",
    body: "Applicable settlement terms, charges, deductions, and restrictions shall be disclosed through relevant service terms, agreements, or platform interfaces.",
  },
  {
    title: "5.3 Traceability",
    body: "Settlement operations shall maintain adequate records to support investigation, reconciliation, and audit.",
  },
  {
    title: "5.4 Security",
    body: "Payouts shall be subject to appropriate authentication, verification, authorization, and fraud prevention controls.",
  },
  {
    title: "5.5 Regulatory and Partner Compliance",
    body: "Settlements shall follow applicable law and authorized financial partner arrangements.",
  },
  {
    title: "5.6 Fair Treatment",
    body: "Restrictions, deductions, and adjustments shall be supported by lawful or contractual grounds and applied through appropriate procedures.",
  },
];

const BALANCE_TYPES = [
  {
    label: "7.1 Transaction Wallet",
    tone: "bg-infoBg text-infoText",
    description: (
      <>
        A balance allocated for permitted service transactions such as
        recharge, bill payments, or other authorized platform services. A
        transaction wallet balance shall not automatically be considered
        withdrawable.
      </>
    ),
  },
  {
    label: "7.2 Commission",
    tone: "bg-successBg text-successText",
    description:
      "Commissions earned from qualifying transactions, subject to applicable calculation, validation, adjustment, and payment terms.",
  },
  {
    label: "7.3 Pending",
    tone: "bg-warnBg text-warnText",
    description:
      "Amounts awaiting transaction confirmation, reconciliation, eligibility validation, or other required processing.",
  },
  {
    label: "7.4 Restricted",
    tone: "bg-errorBg text-errorText",
    description:
      "Amounts temporarily unavailable because of a documented risk, legal, contractual, dispute, or operational condition.",
  },
  {
    label: "7.5 Withdrawable",
    tone: "bg-brandNavy text-white",
    description: "Funds expressly eligible for payout under an approved arrangement.",
  },
];

const PAYOUT_STEPS = [
  { title: "Merchant authentication" },
  { title: "Verification of payout eligibility" },
  { title: "Validation of available withdrawable funds" },
  { title: "Confirmation of the registered bank account" },
  { title: "Presentation of any applicable charges" },
  { title: "Security and risk checks" },
  { title: "Submission to the relevant payout provider" },
  { title: "Receipt of the provider's processing result" },
  { title: "Updating the merchant-facing transaction status" },
];

const SETTLEMENT_STATUSES = [
  {
    label: "Requested",
    tone: "bg-surface text-ink700 border border-line",
    description: "The payout request has been submitted.",
  },
  {
    label: "Under Review",
    tone: "bg-warnBg text-warnText",
    description: "The request requires verification or risk review.",
  },
  {
    label: "Approved",
    tone: "bg-infoBg text-infoText",
    description: "The request has been authorized for processing.",
  },
  {
    label: "Processing",
    tone: "bg-infoBg text-infoText",
    description: "The payout is being processed.",
  },
  {
    label: "Successful",
    tone: "bg-successBg text-successText",
    description: "Success has been confirmed using the applicable settlement evidence.",
  },
  {
    label: "Failed",
    tone: "bg-errorBg text-errorText",
    description: "The payout was not completed.",
  },
  {
    label: "Reversed",
    tone: "bg-surfaceBlue text-brandNavy",
    description: "The relevant amount has been returned or adjusted.",
  },
  {
    label: "On Hold",
    tone: "bg-warnBg text-warnText",
    description: "A documented restriction temporarily prevents processing.",
  },
  {
    label: "Cancelled",
    tone: "bg-ink900 text-surfaceAlt",
    description: "The request has been validly cancelled before completion.",
  },
];

export default function MerchantSettlementPolicy() {
  return (
    <LegalLayout
      title="Merchant Settlement & Payout Policy"
      eyebrow="Legal"
      description="How JayesPay records, reconciles, settles and pays out eligible merchant funds, commissions and refunds."
      lastUpdated="26 October 2026"
      toc={TOC}
      testId="merchant-settlement-page"
    >
      <DocumentInfo items={DOCUMENT_INFO} />

      <Section id="introduction" number={1} title="Introduction">
        <p>
          JayesPay (“JayesPay”, “we”, “our”, or “us”) is a retailer-first
          financial technology platform operated as a product of{" "}
          <strong>{COMPANY.legalName}</strong> (“Company”).
        </p>
        <p>
          JayesPay enables eligible merchants and retailers to provide digital
          and assisted financial services, including mobile recharge, bill
          payments, banking correspondent services, AePS, micro-ATM, domestic
          money transfer, Aadhaar Pay, PAN services, and other authorized
          services through its integrated platform.
        </p>
        <p>
          This Merchant Settlement &amp; Payout Policy (“Policy”) establishes
          the general principles governing the recording, reconciliation,
          settlement, withdrawal, refund, adjustment, and payout of eligible
          merchant funds and transaction-related earnings.
        </p>
        <p>
          The Policy is intended to promote transparent financial operations,
          protect merchant interests, reduce financial and operational risks,
          and ensure that settlement activities are conducted in accordance
          with applicable law and authorized banking or payment partner
          arrangements.
        </p>
        <p>
          Settlement services are subject to the actual role, contractual
          authority, and regulatory status of JayesPay and its respective
          service providers.
        </p>
      </Section>

      <Section id="purpose" number={2} title="Purpose and Objectives">
        <p>The objectives of this Policy are to:</p>
        <ol>
          <li>Establish transparent procedures governing merchant settlements and payouts.</li>
          <li>
            Define the treatment of merchant wallet balances, transaction
            proceeds, and earned commissions.
          </li>
          <li>Explain settlement eligibility, initiation, processing, and completion.</li>
          <li>Define appropriate controls for merchant bank-account verification.</li>
          <li>
            Establish procedures for transaction reconciliation, failed
            transactions, refunds, and reversals.
          </li>
          <li>Prevent unauthorized transfers, financial fraud, and settlement misuse.</li>
          <li>
            Explain circumstances under which a settlement may be delayed,
            restricted, or withheld.
          </li>
          <li>
            Support applicable requirements of banking, payment, and financial
            service partners.
          </li>
          <li>Establish a consistent merchant grievance and settlement dispute process.</li>
          <li>Maintain appropriate transaction records and audit trails.</li>
        </ol>
      </Section>

      <Section id="scope" number={3} title="Scope and Applicability">
        <p>
          This Policy applies to merchants and retailers registered with
          JayesPay who are eligible to receive settlements, commissions,
          refunds, or other authorized payouts through the platform.
        </p>
        <p>It covers, where applicable:</p>
        <ul>
          <li>Merchant wallet funding and balance adjustments.</li>
          <li>Merchant commission settlements.</li>
          <li>Authorized bank-account payouts.</li>
          <li>Transaction settlement and reconciliation.</li>
          <li>Refunds and reversals.</li>
          <li>Failed or pending transactions.</li>
          <li>Service-specific settlement arrangements.</li>
          <li>Settlement restrictions and risk-based reviews.</li>
          <li>Merchant account closure and final settlement.</li>
        </ul>
        <Callout>
          This Policy does not create an unconditional right to withdraw every
          balance displayed within the platform.
        </Callout>
        <p>
          Certain services may be governed by additional settlement
          requirements established by participating banks, payment service
          providers, regulated entities, or service partners.
        </p>
      </Section>

      <Section id="definitions" number={4} title="Definitions">
        <dl className="grid sm:grid-cols-2 gap-3 my-5">
          {DEFINITIONS.map(([term, meaning], index) => (
            <div key={term} className="rounded-2xl border border-line bg-surface p-4">
              <dt className="font-bold text-ink900 mb-1">
                <span className="text-brandOrange mr-1.5 tabular-nums">4.{index + 1}</span>
                {term}
              </dt>
              <dd className="text-sm text-ink700">{meaning}</dd>
            </div>
          ))}
        </dl>
      </Section>

      <Section id="settlement-principles" number={5} title="Settlement Principles">
        <p>
          JayesPay shall apply the following principles to eligible merchant
          settlement activities:
        </p>
        <CardGrid items={PRINCIPLES} />
      </Section>

      <Section id="eligibility" number={6} title="Merchant Settlement Eligibility">
        <p>
          A merchant may become eligible for payouts after satisfying
          applicable requirements, which may include:
        </p>
        <ol>
          <li>Approved merchant registration.</li>
          <li>Completion of applicable KYM, KYC, or other verification.</li>
          <li>Active account status.</li>
          <li>Verified payout bank account.</li>
          <li>Completion of service-specific authorization where required.</li>
          <li>Availability of eligible and withdrawable funds.</li>
          <li>Compliance with applicable transaction and payout limits.</li>
          <li>Absence of unresolved restrictions affecting the requested payout.</li>
          <li>Acceptance of relevant merchant and settlement terms.</li>
        </ol>
        <Callout tone="warn">
          <strong>
            Completion of merchant onboarding does not automatically establish
            payout eligibility for every category of recorded balance.
          </strong>
        </Callout>
      </Section>

      <Section id="balance-classification" number={7} title="Merchant Wallet and Balance Classification">
        <p>
          JayesPay may maintain internal wallet and ledger records to support
          merchant financial operations.
        </p>
        <p>
          For operational clarity, recorded balances should be classified
          according to their nature and eligibility.
        </p>
        <BadgeTable rows={BALANCE_TYPES} />

        <h3>7.6 Balance Ownership and Custody</h3>
        <p>
          The legal ownership, custody, safeguarding, and permitted use of
          funds shall be determined by the actual bank, payment service
          provider, regulated entity, contractual, and product arrangements.
        </p>
        <p>
          JayesPay shall not imply that it independently accepts deposits,
          operates a regulated payment instrument, or safeguards customer funds
          under a regulatory authorization unless legally entitled to do so.
        </p>
      </Section>

      <Section id="bank-account" number={8} title="Merchant Bank Account Registration">
        <p>Merchants seeking bank payouts shall register an eligible bank account.</p>
        <p>Required information may include:</p>
        <ul>
          <li>Account holder name.</li>
          <li>Bank account number.</li>
          <li>IFSC.</li>
          <li>Bank name.</li>
          <li>Applicable account verification information.</li>
        </ul>

        <h3>8.1 Account Verification</h3>
        <p>
          JayesPay or its authorized service provider may verify bank account
          ownership through approved verification methods.
        </p>

        <h3>8.2 Account Ownership</h3>
        <p>
          Payout bank accounts shall normally belong to the registered merchant
          or approved legal business entity.
        </p>
        <p>
          Payouts to third-party accounts shall not be permitted unless a
          specific lawful and approved arrangement authorizes them.
        </p>

        <h3>8.3 Bank Account Changes</h3>
        <p>Changes to a registered payout bank account may require:</p>
        <ul>
          <li>Additional authentication.</li>
          <li>Ownership verification.</li>
          <li>Risk review.</li>
          <li>Temporary payout restrictions.</li>
          <li>Other appropriate safeguards.</li>
        </ul>
        <p>
          A new bank account shall not automatically become eligible for
          payouts merely because it was added to a profile.
        </p>
      </Section>

      <Section id="payout-methods" number={9} title="Settlement and Payout Methods">
        <p>
          Subject to eligibility, JayesPay may facilitate payouts through
          approved banking or payment rails.
        </p>
        <p>These may include, where supported:</p>
        <ul>
          <li>IMPS.</li>
          <li>NEFT.</li>
          <li>
            Other legally permitted bank transfer methods supported by the
            responsible payment partner.
          </li>
        </ul>
        <p>The availability of a particular method may depend on:</p>
        <ul>
          <li>The merchant&apos;s bank.</li>
          <li>Payment provider support.</li>
          <li>Applicable transaction limits.</li>
          <li>Operational availability.</li>
          <li>Risk controls.</li>
          <li>Banking and regulatory restrictions.</li>
        </ul>
        <p>
          JayesPay does not guarantee that all payout methods will be available
          to every merchant.
        </p>
      </Section>

      <Section id="settlement-cycles" number={10} title="Settlement Cycles and Processing Timelines">
        <p>Different financial activities may follow different settlement cycles.</p>
        <p>
          The applicable schedule shall be determined by the actual service
          arrangement, participating financial institution, and merchant terms.
        </p>

        <h3>10.1 Merchant Payouts</h3>
        <p>
          Eligible payout requests shall be processed according to the
          published or contractually applicable payout schedule.
        </p>

        <h3>10.2 Commission Settlements</h3>
        <p>
          Commission payments may require validation of the underlying
          transactions and completion of relevant reconciliation procedures.
        </p>

        <h3>10.3 Service-Specific Settlement</h3>
        <p>
          Certain banking, cash, payment, or financial services may operate on
          partner-defined settlement cycles.
        </p>

        <h3>10.4 Processing Delays</h3>
        <p>Settlement processing may be affected by:</p>
        <ul>
          <li>Banking holidays.</li>
          <li>Payment network interruptions.</li>
          <li>Service provider downtime.</li>
          <li>Incorrect bank account details.</li>
          <li>Pending reconciliation.</li>
          <li>Transaction disputes.</li>
          <li>Security alerts.</li>
          <li>Regulatory or lawful authority restrictions.</li>
        </ul>
        <p>
          JayesPay shall provide accurate information about known processing
          delays where appropriate and reasonably practicable.
        </p>

        <h3>10.5 Published Settlement Schedule</h3>
        <p>
          Approved settlement windows, cutoff times, limits, and processing
          fees shall be communicated through the relevant merchant agreement,
          service terms, or platform interface.
        </p>
        <Callout tone="warn">
          <strong>
            No universal T+0, T+1, instant payout, or guaranteed settlement
            commitment shall apply unless expressly established for the
            relevant product and supported by the responsible payment
            arrangement.
          </strong>
        </Callout>
      </Section>

      <Section id="payout-requests" number={11} title="Merchant Payout Requests">
        <p>
          Where manual payouts are supported, merchants may submit requests
          using the authorized JayesPay interface.
        </p>
        <p>A payout request may involve:</p>
        <StepTimeline steps={PAYOUT_STEPS} />
        <p>
          Submitting a request does not guarantee that the receiving bank has
          credited the amount.
        </p>
        <p>
          JayesPay shall distinguish between request acceptance, payout
          initiation, processing, success, and failure.
        </p>
      </Section>

      <Section id="status-tracking" number={12} title="Settlement Status and Tracking">
        <p>
          Where applicable, merchants may view the status of their settlement
          requests.
        </p>
        <BadgeTable rows={SETTLEMENT_STATUSES} />
        <p>
          Settlement statuses shall be supported by appropriate provider
          responses and reconciliation records.
        </p>
        <Callout>
          A successful API request or HTTP response alone shall not be treated
          as conclusive evidence of a completed bank credit.
        </Callout>
      </Section>

      <Section id="charges" number={13} title="Settlement Charges and Deductions">
        <p>
          JayesPay may apply settlement-related fees only where permitted by
          law and the applicable merchant agreement.
        </p>
        <p>Potential charges may include:</p>
        <ul>
          <li>Disclosed payout processing charges.</li>
          <li>Applicable taxes.</li>
          <li>Contractually permitted adjustments.</li>
          <li>Other authorized service-specific charges.</li>
        </ul>
        <p>
          Charges must be communicated before or as part of the applicable
          transaction authorization process.
        </p>
        <p>
          <strong>Unauthorized, undisclosed, or arbitrary deductions are prohibited.</strong>
        </p>
        <p>
          Applicable fees shall be displayed separately where feasible to
          support transparency.
        </p>
      </Section>

      <Section id="commission-settlement" number={14} title="Commission Settlement">
        <p>
          Merchant commissions shall be governed by the applicable
          service-specific commission structure.
        </p>

        <h3>14.1 Commission Eligibility</h3>
        <p>
          Commissions may become payable only after the qualifying transaction
          meets the applicable conditions.
        </p>

        <h3>14.2 Commission Validation</h3>
        <p>Commission records may be validated against:</p>
        <ul>
          <li>Transaction status.</li>
          <li>Provider confirmation.</li>
          <li>Service eligibility.</li>
          <li>Applicable commercial rates.</li>
          <li>Refunds and reversals.</li>
          <li>Fraud and misuse checks.</li>
        </ul>

        <h3>14.3 Commission Adjustments</h3>
        <p>
          Commissions associated with invalid, cancelled, reversed,
          duplicated, or fraudulent transactions may be adjusted where
          permitted under the relevant terms.
        </p>

        <h3>14.4 Commission Statements</h3>
        <p>
          JayesPay should provide merchants with accessible commission and
          adjustment records showing the relevant calculation and transaction
          references.
        </p>

        <h3>14.5 Taxes</h3>
        <p>
          Applicable taxes, tax deduction obligations, and other statutory
          requirements shall be determined according to the nature of the
          payment, the merchant&apos;s status, and applicable tax law.
        </p>
      </Section>

      <Section
        id="financial-service-settlements"
        number={15}
        title="Banking and Financial Service Settlements"
      >
        <p>
          Certain JayesPay services require additional settlement controls due
          to their financial nature.
        </p>

        <h3>15.1 AePS and Aadhaar Pay</h3>
        <p>
          Settlements and transaction accounting involving AePS or Aadhaar Pay
          shall be subject to applicable bank, acquiring institution, NPCI, and
          other authorized participant requirements.
        </p>
        <p>
          Any cash-related merchant adjustment or entitlement shall arise only
          through a valid, authorized, and reconciled transaction arrangement.
        </p>
        <p>
          Merchants shall not initiate fictitious transactions to obtain funds
          or create artificial settlement balances.
        </p>

        <h3>15.2 Micro-ATM</h3>
        <p>
          Micro-ATM settlements shall follow the relevant acquiring, issuing,
          and payment network arrangements.
        </p>
        <p>
          Cash disbursed or received during a customer transaction must be
          properly recorded and reconciled.
        </p>
        <p>
          Merchant commissions, reimbursements, and cash-position adjustments
          shall be separately identifiable where the operating model requires
          them.
        </p>

        <h3>15.3 Domestic Money Transfer</h3>
        <p>
          DMT settlements and refunds shall follow the applicable remittance
          provider and banking partner requirements.
        </p>
        <p>
          Merchants shall not claim completion of a money transfer without
          valid confirmation through the authorized system.
        </p>

        <h3>15.4 Banking Correspondent Services</h3>
        <p>
          BC-related customer funds, cash balances, commissions,
          reimbursements, and settlements shall be governed by the appointing
          bank&apos;s applicable operating procedures and contractual
          arrangements.
        </p>
        <Callout tone="warn">
          <strong>
            JayesPay shall not treat funds belonging to banking customers as
            unrestricted merchant wallet funds.
          </strong>
        </Callout>

        <h3>15.5 Bill Payments and Recharge</h3>
        <p>
          Payment processing, provider acknowledgments, failed transactions,
          and reversals shall be handled according to the relevant bill
          payment or recharge provider arrangements.
        </p>

        <h3>15.6 Loans, Insurance, and Other Financial Products</h3>
        <p>
          Commissions or other eligible payments for distribution, referrals,
          or assisted services shall follow applicable partner terms, product
          regulations, and validation requirements.
        </p>
      </Section>

      <Section id="reconciliation" number={16} title="Transaction Reconciliation">
        <p>JayesPay shall maintain appropriate financial reconciliation procedures.</p>
        <p>Reconciliation may involve comparison of:</p>
        <ul>
          <li>Internal transaction records.</li>
          <li>Merchant wallet or ledger entries.</li>
          <li>Banking partner records.</li>
          <li>Payment processor records.</li>
          <li>Service provider reports.</li>
          <li>Settlement and payout confirmations.</li>
          <li>Refund and reversal records.</li>
          <li>Commission records.</li>
        </ul>

        <h3>16.1 Reconciliation Frequency</h3>
        <p>
          The frequency shall be established according to transaction volume,
          risk, service characteristics, and partner obligations.
        </p>

        <h3>16.2 Reconciliation Differences</h3>
        <p>
          Unmatched, duplicate, inconsistent, or otherwise disputed entries
          shall be identified and investigated.
        </p>

        <h3>16.3 Financial Corrections</h3>
        <p>
          Corrections shall be made through controlled accounting adjustments
          supported by evidence.
        </p>
        <p>
          Transactions shall not be silently deleted or overwritten to conceal
          financial discrepancies.
        </p>

        <h3>16.4 Approval Controls</h3>
        <p>
          Sensitive financial adjustments shall require appropriate
          authorization and audit records.
        </p>
      </Section>

      <Section id="failed-transactions" number={17} title="Failed, Pending, and Reversed Transactions">
        <p>
          Financial transactions may fail or remain pending because of
          technical, banking, network, or operational conditions.
        </p>

        <h3>17.1 Failed Transactions</h3>
        <p>
          Where a transaction has definitively failed, the applicable provider
          and settlement rules shall determine the necessary reversal or
          refund.
        </p>

        <h3>17.2 Pending Transactions</h3>
        <p>
          A pending transaction shall not automatically be treated as failed or
          eligible for immediate refund.
        </p>

        <h3>17.3 Duplicate Transactions</h3>
        <p>
          Verified duplicate financial entries shall be investigated and
          corrected under the applicable process.
        </p>

        <h3>17.4 Reversal Processing</h3>
        <p>
          Reversals may require confirmation from the relevant financial or
          service provider.
        </p>

        <h3>17.5 Merchant Communication</h3>
        <p>Merchants should receive appropriate status information and reference details.</p>

        <h3>17.6 Mandatory Timelines</h3>
        <p>
          Where a specific regulatory or payment-network timeline applies to a
          failed transaction, reversal, or customer compensation, that
          applicable timeline shall prevail over any general platform
          processing schedule.
        </p>
      </Section>

      <Section id="refunds" number={18} title="Refunds and Wallet Adjustments">
        <p>Refunds, wallet credits, and balance adjustments may arise from:</p>
        <ul>
          <li>Failed transactions.</li>
          <li>Duplicate debits.</li>
          <li>Provider reversals.</li>
          <li>Approved service refunds.</li>
          <li>Incorrect financial postings.</li>
          <li>Other legitimate correction events.</li>
        </ul>
        <p>
          The destination and method of refund shall depend on the original
          funding mechanism, applicable partner rules, statutory obligations,
          and approved product design. See also our{" "}
          <a href="/refund-policy">Refund Policy</a>.
        </p>
        <p>A wallet credit does not necessarily confer an unrestricted right to withdraw funds.</p>
        <p>
          JayesPay shall not arbitrarily substitute wallet credits for a refund
          to the original payment source where the governing rules require
          otherwise.
        </p>
      </Section>

      <Section id="holds" number={19} title="Settlement Holds and Restrictions">
        <p>
          JayesPay or the responsible financial provider may restrict
          settlements where justified by:
        </p>
        <ul>
          <li>Suspected fraud.</li>
          <li>Unauthorized transactions.</li>
          <li>Material account verification discrepancies.</li>
          <li>Confirmed or reasonably suspected account compromise.</li>
          <li>Applicable sanctions or legal prohibitions.</li>
          <li>A lawful authority&apos;s direction.</li>
          <li>Service provider or banking partner restrictions.</li>
          <li>Pending financial reconciliation.</li>
          <li>Contractual reserves or disputed obligations.</li>
          <li>Other documented material financial risks.</li>
        </ul>

        <h3>19.1 Scope of Restriction</h3>
        <p>
          Where operationally and legally feasible, restrictions should be
          limited to the affected amount, transaction, or service rather than
          unrelated legitimate balances.
        </p>

        <h3>19.2 Review</h3>
        <p>Restrictions shall be subject to appropriate review and escalation.</p>

        <h3>19.3 Merchant Notification</h3>
        <p>
          Merchants shall be notified where legally permitted and operationally
          appropriate.
        </p>

        <h3>19.4 Release of Funds</h3>
        <p>
          Eligible funds shall be released when the grounds for restriction
          have been resolved and the responsible provider or authority permits
          release.
        </p>
        <Callout>
          JayesPay shall not impose indefinite or arbitrary holds without a
          lawful or contractual basis.
        </Callout>
      </Section>

      <Section id="negative-balances" number={20} title="Negative Balances and Financial Recoveries">
        <p>
          A negative merchant balance may arise from legitimate corrections,
          reversals, permitted chargebacks, duplicate credits, or other valid
          financial adjustments.
        </p>
        <p>Any resulting recovery shall be supported by:</p>
        <ul>
          <li>An identifiable underlying financial obligation.</li>
          <li>Accurate records.</li>
          <li>Applicable contractual or legal authority.</li>
          <li>Appropriate merchant communication.</li>
          <li>Authorized accounting procedures.</li>
        </ul>
        <p>
          JayesPay may restrict certain activities while a legitimate
          outstanding obligation remains unresolved, where legally and
          contractually permitted.
        </p>
        <p>
          Negative balances shall not be created through unsupported or
          unexplained deductions.
        </p>
      </Section>

      <Section id="settlement-security" number={21} title="Fraud Prevention and Settlement Security">
        <p>
          JayesPay shall implement risk-based settlement security controls,
          which may include:
        </p>
        <ul>
          <li>Verified payout beneficiary accounts.</li>
          <li>Secure merchant authentication.</li>
          <li>T-PIN or approved additional transaction authentication.</li>
          <li>Device and session monitoring.</li>
          <li>Duplicate payout prevention.</li>
          <li>Transaction velocity checks.</li>
          <li>Suspicious beneficiary-change detection.</li>
          <li>Settlement approval controls.</li>
          <li>Risk-based transaction limits.</li>
          <li>Audit logging.</li>
          <li>Partner confirmation and reconciliation.</li>
        </ul>
        <p>
          Financial operations shall be designed to reduce the risk of
          unauthorized payouts, duplicate payments, account takeover, and
          ledger manipulation.
        </p>
        <p>
          Merchant passwords, OTPs, PINs, and sensitive credentials must not be
          shared with unauthorized persons.
        </p>
      </Section>

      <Section id="merchant-responsibilities" number={22} title="Merchant Responsibilities">
        <p>Merchants are responsible for:</p>
        <ol>
          <li>Maintaining accurate banking details.</li>
          <li>Protecting their account credentials.</li>
          <li>Reviewing transaction and settlement records.</li>
          <li>Reporting suspected unauthorized payouts.</li>
          <li>Notifying JayesPay of material settlement discrepancies.</li>
          <li>Following applicable service-specific cash and settlement procedures.</li>
          <li>Cooperating with reasonable verification and investigations.</li>
          <li>Avoiding misuse of customer funds.</li>
          <li>Complying with applicable financial service agreements.</li>
          <li>Maintaining records necessary for their own legal and tax obligations.</li>
        </ol>
        <p>
          Merchants shall not artificially inflate transaction volumes,
          fabricate transactions, manipulate cash-related records, or attempt
          unauthorized withdrawal of restricted balances.
        </p>
      </Section>

      <Section id="platform-responsibilities" number={23} title="Platform Responsibilities">
        <p>
          Within its actual contractual and operational role, JayesPay shall
          endeavor to:
        </p>
        <ol>
          <li>Maintain accurate merchant-facing financial records.</li>
          <li>Facilitate approved payout instructions through authorized providers.</li>
          <li>Implement appropriate security controls.</li>
          <li>Maintain reconciliation procedures.</li>
          <li>Record significant financial adjustments.</li>
          <li>Provide settlement status visibility.</li>
          <li>Investigate reported discrepancies.</li>
          <li>Coordinate with responsible payment and banking partners.</li>
          <li>Protect financial information.</li>
          <li>Comply with applicable legal and contractual obligations.</li>
        </ol>
        <p>
          JayesPay shall not represent itself as guaranteeing a bank or payment
          provider&apos;s performance unless such a guarantee is expressly
          lawful, authorized, and contractually undertaken.
        </p>
      </Section>

      <Section id="disputes" number={24} title="Settlement Disputes and Grievance Redressal">
        <p>Merchants may report settlement-related concerns, including:</p>
        <ul>
          <li>Delayed payouts.</li>
          <li>Failed transfers.</li>
          <li>Incorrect settlement amounts.</li>
          <li>Missing commissions.</li>
          <li>Duplicate deductions.</li>
          <li>Unexplained balance adjustments.</li>
          <li>Unauthorized payout activity.</li>
          <li>Pending or disputed reversals.</li>
        </ul>
        <p>
          Complaints should include relevant transaction references, dates,
          amounts, and supporting information where available.
        </p>
        <p>
          JayesPay shall investigate complaints and coordinate with responsible
          financial partners where required.
        </p>
        <p>
          Applicable statutory, payment-network, or regulated-partner complaint
          and resolution timelines shall be followed.
        </p>
        <p>
          Merchants shall retain access to any legally available external
          grievance or regulatory escalation mechanism.
        </p>
        <SupportCard title="JayesPay Merchant Support" />
      </Section>

      <Section id="account-closure" number={25} title="Merchant Account Closure and Final Settlement">
        <p>
          Upon closure, suspension, or termination of a merchant account,
          JayesPay shall determine the status of eligible outstanding balances
          and financial obligations.
        </p>
        <p>The process may involve:</p>
        <ul>
          <li>Verification of the requesting account holder.</li>
          <li>Identification of pending transactions.</li>
          <li>Reconciliation of commissions and adjustments.</li>
          <li>Assessment of lawful restrictions.</li>
          <li>Resolution of outstanding financial obligations.</li>
          <li>Validation of the payout beneficiary account.</li>
          <li>
            Release or return of eligible funds through the appropriate
            provider or mechanism.
          </li>
        </ul>
        <Callout>
          Legitimate merchant funds shall not automatically be forfeited upon
          account closure.
        </Callout>
        <p>
          Any unclaimed, restricted, disputed, or otherwise exceptional balance
          shall be handled according to applicable law and the relevant
          financial arrangement. See also{" "}
          <a href="/account-deactivation">Account Deactivation</a>.
        </p>
      </Section>

      <Section id="records" number={26} title="Financial Records and Audit">
        <p>JayesPay shall maintain appropriate records relating to:</p>
        <ul>
          <li>Merchant wallet entries.</li>
          <li>Transaction processing.</li>
          <li>Settlement requests.</li>
          <li>Payout approvals.</li>
          <li>Bank or provider references.</li>
          <li>Commission calculations.</li>
          <li>Refunds and reversals.</li>
          <li>Financial adjustments.</li>
          <li>Restrictions and approvals.</li>
          <li>Dispute investigations.</li>
        </ul>
        <p>
          Records shall be retained according to applicable legal, financial,
          contractual, and data protection requirements.
        </p>
        <p>Access to sensitive financial information shall be appropriately restricted.</p>
      </Section>

      <Section id="changes" number={27} title="Changes to Settlement Arrangements">
        <p>
          JayesPay may revise settlement methods, fees, cycles, limits, or
          related conditions where permitted by law and the applicable
          agreements.
        </p>
        <p>
          Material changes shall be communicated through an appropriate notice
          mechanism and within any legally required notice period.
        </p>
        <p>
          Changes shall not retrospectively deprive merchants of lawfully
          accrued entitlements.
        </p>
      </Section>

      <Section id="governing-law" number={28} title="Governing Law">
        <p>This Policy shall be governed by applicable laws of India.</p>
        <p>
          Disputes shall be subject to the relevant merchant agreement&apos;s
          lawful dispute resolution arrangements, without limiting mandatory
          legal, consumer, or regulatory rights.
        </p>
      </Section>

      <Section id="acceptance" number={29} title="Policy Acceptance">
        <p>
          Use of payout and settlement features shall be subject to the
          merchant&apos;s acceptance of the applicable contractual terms and
          fulfillment of the required eligibility and verification conditions.
        </p>
        <p>
          This Policy shall be read in conjunction with the JayesPay{" "}
          <a href="/merchant-onboarding-policy">Merchant Onboarding Policy</a>,
          Merchant <a href="/terms-and-conditions">Terms and Conditions</a>,{" "}
          <a href="/privacy-policy">Privacy Policy</a>, and applicable
          service-specific agreements.
        </p>
        <p>
          In the event of a conflict with a mandatory legal requirement, the
          applicable law shall prevail.
        </p>
      </Section>

      <Section id="company-information" number={30} title="Company Information">
        <CompanyCard />
      </Section>
    </LegalLayout>
  );
}
